import { signalStoreFeature, withMethods, patchState, withState, withHooks } from '@ngrx/signals';
import { withEntities, setAllEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, Observable } from 'rxjs';
import { ProviderToken, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { safeErrorSummary } from '../helper/safe-error-summary';

export interface WithEntitiesStoreOptions {
  /**
   * Invoked when `Service.load()` errors. Defaults to `console.error`. Pass a
   * handler to surface the failure elsewhere (e.g. a toast) without each store
   * re-implementing the load/error boilerplate. Loading is always cleared via
   * `finalize` regardless of this callback.
   */
  onError?: (error: unknown) => void;
}

export const withEntitiesStore = <Entity extends { id: string }>(
  collection: string,
  Service: ProviderToken<{ load: () => Observable<Entity[]> }>,
  options?: WithEntitiesStoreOptions,
) =>
  signalStoreFeature(
    withState({ loading: true }),
    withEntities<Entity>(),
    withMethods((s, loader = inject(Service)) => ({
      load: rxMethod<void>(
        pipe(
          switchMap(
            () => (
              patchState(s, { loading: true }),
              loader.load().pipe(
                tapResponse({
                  next: entities => patchState(s, setAllEntities(entities)),
                  error: (error: unknown) => (options?.onError ?? (e => console.error(safeErrorSummary(e))))(error),
                  finalize: () => patchState(s, { loading: false }),
                }),
              )
            ),
          ),
        ),
      ),
    })),
    withHooks({
      onInit: s => s.load(),
    }),
  );
